import * as React from 'react'
import styled from 'styled-components'
import { NextSeo } from 'next-seo'

import { Container, BreadCrumbs, breakpoint, styles as S } from '@bob/design-system'
import * as entity from '@boutique/entities'
import * as url from '@bob/url'

import { SITE_NAME, SITE_URL } from '@bob/config'

import { TagList } from '@bob/components'

import { Head } from './Head'
import { Content } from './Content'
import { Author } from './Author'
import { Similar } from './Similar'

export type ArticleProps = {
  article: entity.article.Article
}

export function Article({ article }: ArticleProps): React.ReactElement {
  const pageUrl = url.article({ slug: article.slug, title: article.title })

  const [readTime, setReadTime] = React.useState<null | string>(null)

  return (
    <>
      <NextSeo
        title={`${article.title} | ${SITE_NAME}`}
        description={article.hat || undefined}
        canonical={SITE_URL + pageUrl.path}
        openGraph={{
          title: `${article.title} | ${SITE_NAME}`,
          description: article.hat || undefined,
          url: SITE_URL + pageUrl.path,
          images: [
            {
              alt: article.seo.alt ?? undefined,
              height: article.seo.height ?? undefined,
              url: article.seo.src,
              width: article.seo.width ?? undefined,
            },
          ],
          type: 'article',
          article: {
            publishedTime: article.firstPublishedAt ?? undefined,
            tags: [article.section, ...article.tags.map((tag) => tag.name)],
          },
        }}
        robotsProps={{
          maxImagePreview: 'large',
        }}
      />
      <Container>
        <BreadCrumbs crumbs={pageUrl.crumbs} />
        <Section>
          <Head article={article} readTime={readTime} />
          <Content article={article} setReadTime={setReadTime} url={SITE_URL + pageUrl.path} />
          <Author article={article} />
          {article.tags.length > 0 && (
            <Block>
              <S.heading.TitleCardArticle as="h3">Tags associés à l’article</S.heading.TitleCardArticle>
              <TagList context="article" tagList={article.tags} />
            </Block>
          )}
        </Section>
        <Similar article={article} />
      </Container>
    </>
  )
}

const Section = styled.section`
  margin: 0 auto;
  max-width: 1060px;

  @media screen and (${breakpoint.TABLET}) {
    margin-top: 32px;
    position: relative;
  }
`

const Block = styled.section`
  ${S.heading.TitleCardArticle} {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
  }
`
